import { AsyncSelect, Button, Form, FormField, MeasurementInput, ModalDialog } from "@rothoblaas/design-system";
import { FC, useContext } from "react";
import { Control, Controller, useWatch } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useAsyncOptions from "../../../../hooks/useAsyncOptions";
import useI18n from "../../../../hooks/useTranslations";
import {
  ChemicalAnchorMapping,
  InsertChemicalAnchorMappingBody,
} from "../../../../http/concreteResin/types/chemicalAnchorMappings";
import { ChemicalAnchor } from "../../../../http/concreteResin/types/chemicalAnchors";
import useChemicalAnchorMappingsApi from "../../../../http/concreteResin/useChemicalAnchorMappings";
import useChemicalAnchorsApi from "../../../../http/concreteResin/useChemicalAnchors";
import { toUsDrillBitInches } from "../resinUnits";

interface ChemicalAnchorMappingFormBody {
  chemicalAnchor: ChemicalAnchor | null;
  rodDiameter: number | null;
  holeDiameter: number | null;
}

interface ChemicalAnchorMappingFormProps {
  mapping?: ChemicalAnchorMapping;
  onClose: () => void;
}

// The US drill bit imperial calculations drill the mapping's hole with: computed from the hole as it's
// typed (toUsDrillBitInches), read-only. Its own component, to watch the hole diameter.
const UsDrillBitField: FC<{ control: Control<ChemicalAnchorMappingFormBody> }> = ({ control }) => {
  const i18n = useI18n();
  const holeDiameter = useWatch({ control, name: "holeDiameter" });

  return (
    <FormField
      label={i18n.translation.concreteResinModule.holeDiameterImperial}
      info={i18n.translation.concreteResinModule.holeDiameterImperialInfo}
      isOptional
    >
      {({ labelId }) => (
        <MeasurementInput
          id={labelId}
          system="imperial"
          unit="in"
          maxDenominator={64}
          decimalScale={6}
          value={holeDiameter != null && holeDiameter > 0 ? toUsDrillBitInches(holeDiameter) : null}
          disabled
        />
      )}
    </FormField>
  );
};

const ChemicalAnchorMappingForm: FC<ChemicalAnchorMappingFormProps> = ({ mapping, onClose }) => {
  const i18n = useI18n();
  const resinTranslation = i18n.translation.concreteResinModule;
  const dispatch = useContext(NotificationDispatch);
  const mappingsApi = useChemicalAnchorMappingsApi();
  const chemicalAnchorsApi = useChemicalAnchorsApi();
  const chemicalAnchorOptions = useAsyncOptions(chemicalAnchorsApi.searchChemicalAnchors);

  const defaultValues: ChemicalAnchorMappingFormBody = {
    chemicalAnchor: mapping?.chemicalAnchor ?? null,
    rodDiameter: mapping?.rodDiameter ?? null,
    holeDiameter: mapping?.holeDiameter ?? null,
  };

  const onSubmit = () => async (formData: ChemicalAnchorMappingFormBody) => {
    if (!formData.chemicalAnchor || formData.rodDiameter == null || formData.holeDiameter == null) return;

    const body: InsertChemicalAnchorMappingBody = {
      chemicalAnchor: { item: { id: formData.chemicalAnchor.item.id } },
      rodDiameter: formData.rodDiameter,
      holeDiameter: formData.holeDiameter,
    };

    try {
      if (mapping) {
        await mappingsApi.updateChemicalAnchorMapping(mapping.id, body);
      } else {
        await mappingsApi.insertChemicalAnchorMapping(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={`${mapping ? i18n.translation.common.edit : i18n.translation.common.add} ${resinTranslation.anchorMapping}`}
    >
      <Form<ChemicalAnchorMappingFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            {/* A mapping keeps its anchor: only its diameters change. */}
            <Controller
              name="chemicalAnchor"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={resinTranslation.chemicalAnchor} error={fieldState.error}>
                  {({ labelId }) => (
                    <AsyncSelect<ChemicalAnchor, false>
                      {...field}
                      {...chemicalAnchorOptions}
                      inputId={labelId}
                      isMulti={false}
                      isDisabled={!!mapping}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => `${option.item.code} - ${option.item.description}`}
                      getOptionValue={(option) => option.id}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="rodDiameter"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={resinTranslation.rodDiameter} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <MeasurementInput
                      id={labelId}
                      unit={i18n.translation.common.unitMm}
                      value={field.value}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="holeDiameter"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={resinTranslation.holeDiameter} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <MeasurementInput
                      id={labelId}
                      unit={i18n.translation.common.unitMm}
                      value={field.value}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <UsDrillBitField control={control} />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default ChemicalAnchorMappingForm;
